import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchYmrsAssessment } from "../../../store/slices/ymrsAssessmentsSlice";
import logo from "../../../assets/images/logo-samplee.png";

export default function YMRSFormPrint() {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const { currentYmrsAssessment, isLoading, error } = useSelector((state) => state.ymrsAssessments || {});

    useEffect(() => {
        if (id) dispatch(fetchYmrsAssessment(id));
    }, [dispatch, id]);

    useEffect(() => {
        if (currentYmrsAssessment) {
            const timer = setTimeout(() => window.print(), 1000);
            window.onafterprint = () => navigate(-1);
            return () => {
                clearTimeout(timer);
                window.onafterprint = null;
            };
        }
    }, [currentYmrsAssessment, navigate]);

    if (isLoading) return <div className="p-8 text-center">Loading YMRS Report...</div>;
    if (error) return <div className="p-8 text-danger text-center">{error}</div>;
    if (!currentYmrsAssessment) return <div className="p-8 text-center">No record found.</div>;

    const d = currentYmrsAssessment;

    // Mapping based on your backend response keys
    const ymrsItems = [
        { title: "Elevated Mood", scoreKey: "elevated_mood_score", valueKey: "elevated_mood_value" },
        { title: "Increased Motor Activity", scoreKey: "motor_activity_score", valueKey: "motor_activity_value" },
        { title: "Sexual Interest", scoreKey: "sexual_interest_score", valueKey: "sexual_interest_value" },
        { title: "Sleep", scoreKey: "sleep_score", valueKey: "sleep_value" },
        { title: "Irritability", scoreKey: "irritability_score", valueKey: "irritability_value" },
        { title: "Speech (Rate and Amount)", scoreKey: "speech_score", valueKey: "speech_value" },
        { title: "Language/Thought Disorder", scoreKey: "thought_disorder_score", valueKey: "thought_disorder_value" },
        { title: "Thought Content", scoreKey: "thought_content_score", valueKey: "thought_content_value" },
        { title: "Disruptive/Aggressive Behavior", scoreKey: "disruptive_behavior_score", valueKey: "disruptive_behavior_value" },
        { title: "Appearance", scoreKey: "appearance_score", valueKey: "appearance_value" },
        { title: "Insight", scoreKey: "insight_score", valueKey: "insight_value" }
    ];

    const totalScore = ymrsItems.reduce((sum, item) => sum + (parseInt(d[item.scoreKey] || 0, 10)), 0);

    const getSeverity = (score) => {
        if (score <= 12) return "Minimal Symptoms";
        if (score <= 20) return "Mild Mania";
        if (score <= 30) return "Moderate Mania";
        return "Severe Mania";
    };

    return (
        <>
            <style jsx global>{`
                * { margin: 0 !important; padding: 0 !important; box-sizing: border-box !important; font-family: Arial, sans-serif !important; }
                body { background: #fff !important; color: #000 !important; font-size: 11px !important; }
                .print-container { background: #fff !important; position: relative !important; }
                .logo { position: absolute !important; top: 15px !important; left: 15px !important; }
                .logo img { height: 60px !important; width: auto !important; }
                .header { text-align: center !important; margin-bottom: 20px !important; border-bottom: 2px solid #000 !important; padding-bottom: 10px !important; }
                .header h3 { font-size: 1.5rem !important; font-weight: bold !important; }
                .meta-table { width: 100% !important; border-collapse: collapse !important; margin-bottom: 15px !important; }
                .meta-table td { border: 1px solid #000 !important; padding: 6px !important; }
                .label-bg { background: #f2f2f2 !important; font-weight: bold !important; width: 18% !important; }
                .ymrs-table { width: 100% !important; border-collapse: collapse !important; }
                .ymrs-table th, .ymrs-table td { border: 1px solid #000 !important; padding: 6px 10px !important; }
                .ymrs-table th { background: #eee !important; font-weight: bold !important; text-align: center !important; }
                .score-col { width: 60px !important; text-align: center !important; font-weight: bold !important; font-size: 13px !important; }
                .category-title { font-weight: bold !important; background: #fafafa !important; width: 160px !important; }
                .result-summary { margin-top: 25px !important; border: 2px solid #000 !important; display: flex !important; page-break-inside: avoid !important; }
                .result-score { background: #000 !important; color: #fff !important; padding: 15px 30px !important; text-align: center !important; min-width: 130px !important; }
                .result-score h2 { font-size: 2.2rem !important; margin: 0 !important; }
                .result-text { flex-grow: 1 !important; padding: 15px !important; text-align: center !important; display: flex !important; flex-direction: column !important; justify-content: center !important; }
                .severity-label { font-size: 1.4rem !important; font-weight: bold !important; text-decoration: underline !important; text-transform: uppercase !important; }
                .footer { margin-top: 30px !important; border-top: 1px solid #000 !important; padding-top: 10px !important; text-align: center !important; font-size: 10px !important; }
                .sig-row { display: flex !important; justify-content: space-between !important; margin-top: 50px !important; }
                .sig-box { border-top: 1px solid #000 !important; width: 220px !important; text-align: center !important; padding-top: 5px !important; font-weight: bold !important; }
                @media print { .ymrs-table tr { page-break-inside: avoid !important; } }
            `}</style>

            <div className="print-container">
                <div className="logo"><img src={logo} alt="Logo" /></div>
                <div className="header">
                    <h3>THE RECOVERY HOUSE</h3>
                    <h6>YOUNG MANIA RATING SCALE (YMRS) REPORT</h6>
                </div>

                <table className="meta-table">
                    <tbody>
                        <tr>
                            <td className="label-bg">Client Name</td>
                            <td>{d.client_name}</td>
                            <td className="label-bg">Date</td>
                            <td>{new Date(d.created_at).toLocaleDateString('en-GB')}</td>
                            <td className="label-bg">Assessed By</td>
                            <td colSpan="3">{d.assessed_by}</td>
                        </tr>
                    </tbody>
                </table>

                <table className="ymrs-table">
                    <thead>
                        <tr>
                            <th style={{ width: '35px' }}>#</th>
                            <th className="category-title">Mania Symptom</th>
                            <th>Clinician Observation / Patient Response</th>
                            <th className="score-col">Score</th>
                        </tr>
                    </thead>
                    <tbody>
                        {ymrsItems.map((item, index) => (
                            <tr key={index}>
                                <td style={{ textAlign: 'center' }}>{index + 1}</td>
                                <td className="category-title">{item.title}</td>
                                <td>{d[item.valueKey]}</td>
                                <td className="score-col">{d[item.scoreKey]}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                {/* <div className="result-summary">
                    <div className="result-score">
                        <small style={{ fontSize: '10px' }}>YMRS TOTAL SCORE</small>
                        <h2>{totalScore}</h2>
                    </div>
                    <div className="result-text">
                        <small style={{ fontSize: '10px' }}>CLINICAL SEVERITY INDICATOR</small>
                        <div className="severity-label">{getSeverity(totalScore)}</div>
                    </div>
                </div> */}

                <div className="sig-row" style={{ marginTop: '60px' }}>
                    <div className="sig-box">Patient Signature</div>
                    <div className="sig-box">Assessing Practitioner Signature</div>
                </div>

                <div className="footer">
                    <p>156 Bangalore Town, Tipu Sultan Road Karachi-Pakistan | Ph: 021-34546364-65-66</p>
                    <p><strong>A Project of “CARVAN OF LIFE PAKISTAN TRUST”</strong></p>
                </div>
            </div>
        </>
    );
}